import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { Button, Card, Empty, Message } from '../admin/ui';
import * as Api from './EmailAdminApi';
import ScheduleCard from './ScheduleCard';
import { errorText, SCHEDULE_KINDS } from './emailAdminModel';

// The "Schedules" view of the E-Mails screen (P4b): one card per automatic email type, stacked in
// one column (the owner's layout rule: no blocks of unpredictable height side by side). The card
// edits its settings locally and saves them explicitly; the days of a countdown are changed by their
// own confirmed actions. A card with unsaved settings reports it, so the screen can ask before a tab
// switch or a link leaves.
//
// The tab reads the URL hash (#schedule-EVENT_REMINDER) the editor's link uses and scrolls to (and
// outlines) that card; no other state lives in the URL.
const EmailSchedules = ({ onDirtyChange }) => {
  const intl = useIntl();
  const { hash } = useLocation();
  const [schedules, setSchedules] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [dirtyKinds, setDirtyKinds] = useState({});

  const load = useCallback(() => {
    setLoadError(null);
    setSchedules(null);
    Api.fetchSchedules()
      .then((list) => setSchedules(Array.isArray(list) ? list : []))
      .catch(setLoadError);
  }, []);

  useEffect(() => { load(); }, [load]);

  const reportDirty = useCallback((kind, dirty) => {
    setDirtyKinds((previous) => (previous[kind] === dirty ? previous : { ...previous, [kind]: dirty }));
  }, []);

  const replace = useCallback((next) => {
    setSchedules((list) => (list || []).map((schedule) => (
      schedule.kind === next.kind ? next : schedule
    )));
  }, []);

  const anyDirty = Object.values(dirtyKinds).some(Boolean);
  useEffect(() => { onDirtyChange(anyDirty); }, [anyDirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange(false), [onDirtyChange]); // eslint-disable-line react-hooks/exhaustive-deps

  // The editor's link back to a card lands on #schedule-<KIND>.
  useEffect(() => {
    if (!schedules || !hash) return;
    const card = document.getElementById(hash.replace(/^#/, ''));
    if (card && typeof card.scrollIntoView === 'function') card.scrollIntoView({ block: 'start' });
  }, [schedules, hash]);

  if (loadError) {
    return (
      <Card>
        <Message error role="alert">
          {errorText(loadError, intl.formatMessage({
            id: 'emailAdmin.schedules.loadFailed',
            defaultMessage: 'Could not load the automatic email schedules.',
          }))}
        </Message>
        <Button type="button" secondary onClick={load}>
          <RefreshCw size={16} /><FormattedMessage id="emailAdmin.refresh" defaultMessage="Refresh" />
        </Button>
      </Card>
    );
  }
  if (!schedules) {
    return (
      <Card>
        <Empty>
          <FormattedMessage id="emailAdmin.schedules.loading" defaultMessage="Loading schedules…" />
        </Empty>
      </Card>
    );
  }
  if (schedules.length === 0) {
    return (
      <Card>
        <Empty>
          <FormattedMessage id="emailAdmin.schedules.empty" defaultMessage="There is no automatic email schedule." />
        </Empty>
      </Card>
    );
  }

  const ordered = [
    ...SCHEDULE_KINDS.map((kind) => schedules.find((schedule) => schedule.kind === kind)).filter(Boolean),
    ...schedules.filter((schedule) => !SCHEDULE_KINDS.includes(schedule.kind)),
  ];

  return (
    <>
      <p style={{ margin: '0 0 1rem', fontSize: '.82rem', color: '#64748b' }}>
        <FormattedMessage id="emailAdmin.schedules.intro"
          defaultMessage="The automatic emails and when they go out. Each card saves its own settings; changing a day is its own confirmed step. The rules are checked by the server." />
      </p>
      {ordered.map((schedule) => (
        <ScheduleCard key={schedule.kind} schedule={schedule} onReplace={replace}
          onDirtyChange={reportDirty} focused={hash === `#schedule-${schedule.kind}`} />
      ))}
    </>
  );
};

export default EmailSchedules;
